'use client'

import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { createContext, useContext, useMemo, useState } from 'react'
import type { Dispatch, ReactNode, SetStateAction } from 'react'
import { ActiveLink } from '../../ActiveLink'

interface AnimatedSubmenuContextValue {
  activeSubmenuId: string | null
  setActiveSubmenuId: Dispatch<SetStateAction<string | null>>
}

const AnimatedSubmenuContext =
  createContext<AnimatedSubmenuContextValue | null>(null)

export const AnimatedSubmenuProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const [activeSubmenuId, setActiveSubmenuId] = useState<string | null>(null)

  const value = useMemo(
    () => ({ activeSubmenuId, setActiveSubmenuId }),
    [activeSubmenuId]
  )

  return (
    <AnimatedSubmenuContext.Provider value={value}>
      {children}
    </AnimatedSubmenuContext.Provider>
  )
}

const useAnimatedSubmenu = () => {
  const context = useContext(AnimatedSubmenuContext)

  if (!context) {
    throw new Error(
      'AnimatedSubmenu must be rendered within AnimatedSubmenuProvider'
    )
  }

  return context
}

interface AnimatedSubmenuProps {
  id: string
  href: string
  label: string
  children: ReactNode
}

export const AnimatedSubmenu = ({
  id,
  href,
  label,
  children,
}: AnimatedSubmenuProps) => {
  const { activeSubmenuId, setActiveSubmenuId } = useAnimatedSubmenu()
  const [dismissed, setDismissed] = useState(false)
  const isActive = activeSubmenuId === id && !dismissed

  const submenuClassName = isActive
    ? 'visible absolute left-[calc(50%-50vw)] top-full w-screen translate-y-0 opacity-100 transition-all duration-200 ease-out'
    : 'pointer-events-none invisible absolute left-[calc(50%-50vw)] top-full w-screen translate-y-2 opacity-0 transition-all duration-150 ease-out'

  return (
    <div
      className="group flex h-full items-center"
      onMouseEnter={() => {
        if (!dismissed) {
          setActiveSubmenuId(id)
        }
      }}
      onClickCapture={event => {
        const target = event.target as HTMLElement
        if (target.closest('a')) {
          setDismissed(true)
          setActiveSubmenuId(null)
        }
      }}
      onMouseLeave={() => {
        setDismissed(false)
        setActiveSubmenuId(currentId => (currentId === id ? null : currentId))
      }}
    >
      <ActiveLink
        href={href}
        className="flex h-full items-center border-b-2 border-transparent text-[16px] xl:text-base hover:border-vi-red hover:text-vi-red"
        activeClassName="border-vi-red text-vi-red"
      >
        {label}{' '}
        <ChevronDownIcon
          className={`ml-1 h-4 w-4 transition-transform duration-200 ${
            isActive ? 'rotate-180' : ''
          }`}
        />
      </ActiveLink>
      <div className={`${submenuClassName} overflow-x-clip`}>
        <div
          className={`flex items-center justify-center transition-transform duration-200 ease-out ${
            isActive ? 'scale-100' : 'scale-[0.995]'
          }`}
        >
          {children}
        </div>
      </div>
    </div>
  )
}
